import { useMemo, useRef, useState } from "react";
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import Scene3D from "./components/Scene3D";
import FovCanvas from "./components/FovCanvas";
import { DEFAULT_PARAMS, buildAngles } from "./geometry/defaults";
import { computeDefaultGeom, computePlanarGeom, computeGimballLockGeom } from "./geometry/vxGeom";
import { commonFovAtZ, estimateGridHalf } from "./geometry/fov";
import "./App.css";

function App() {
  const [params, setParams] = useState(DEFAULT_PARAMS);
  const [showInclined, setShowInclined] = useState(true);
  const [showCoplanar, setShowCoplanar] = useState(false);
  const [showGimbal, setShowGimbal] = useState(false);
  const [theta, setTheta] = useState(0);
  const [showAllFootprints, setShowAllFootprints] = useState(false);
  const sceneRef = useRef(null);

  const updateParam = (key, value) =>
    setParams((prev) => ({ ...prev, [key]: value }));

  const resetAll = () => {
    setParams(DEFAULT_PARAMS);
    setShowInclined(true);
    setShowCoplanar(false);
    setShowGimbal(false);
    setTheta(0);
    setShowAllFootprints(false);
  };

  const angles = useMemo(() => buildAngles(params.numAngles), [params.numAngles]);

  const halfU = params.detWidth / 2;
  const halfV = params.detHeight / 2;

  const geomParams = useMemo(
    () => ({
      sod: params.sod,
      sdd: params.sdd,
      tiltX: params.tiltX,
      tiltY: params.tiltY,
      detPitch: params.detPitch,
      offsetU: params.offsetU,
      offsetV: params.offsetV,
      angles,
    }),
    [params, angles]
  );

  const inclinedVecs = useMemo(() => computeDefaultGeom(geomParams), [geomParams]);
  const coplanarVecs = useMemo(() => computePlanarGeom(geomParams), [geomParams]);
  const gimbalVecs = useMemo(() => computeGimballLockGeom(geomParams), [geomParams]);

  const gridHalf = useMemo(
    () => estimateGridHalf(params.sod, params.sdd, params.detWidth, params.detPitch),
    [params.sod, params.sdd, params.detWidth, params.detPitch]
  );

  const fovResults = useMemo(() => {
    const inclined = commonFovAtZ(inclinedVecs, halfU, halfV, 0);
    const coplanar = commonFovAtZ(coplanarVecs, halfU, halfV, 0);
    const gimbal = commonFovAtZ(gimbalVecs, halfU, halfV, 0);
    return { inclined, coplanar, gimbal };
  }, [inclinedVecs, coplanarVecs, gimbalVecs, halfU, halfV]);

  // Coplanar is the reference setup: every other setup's area is shown as a
  // signed % delta relative to it (negative = smaller FOV than coplanar).
  const relativeToCoplanar = useMemo(() => {
    const base = fovResults.coplanar.area;
    const pct = (area) => (base > 0 ? ((area - base) / base) * 100 : 0);
    return {
      inclined: pct(fovResults.inclined.area),
      gimbal: pct(fovResults.gimbal.area),
    };
  }, [fovResults]);

  return (
    <div className="app-shell">
      <Header />
      <div className="app-body">
        <Sidebar
          params={params}
          onParamChange={updateParam}
          showInclined={showInclined}
          showCoplanar={showCoplanar}
          showGimbal={showGimbal}
          onToggleInclined={() => setShowInclined((v) => !v)}
          onToggleCoplanar={() => setShowCoplanar((v) => !v)}
          onToggleGimbal={() => setShowGimbal((v) => !v)}
          fovResults={fovResults}
          relativeToCoplanar={relativeToCoplanar}
          theta={theta}
          onThetaChange={setTheta}
          onReset={resetAll}
          showAllFootprints={showAllFootprints}
          onToggleAllFootprints={() => setShowAllFootprints((v) => !v)}
          onFocusFov={() => sceneRef.current?.focusFov()}
          onFocusOverall={() => sceneRef.current?.focusOverall()}
        />
        <div className="canvas-column">
          <div className="canvas-pane canvas-pane-top">
            <Scene3D
              ref={sceneRef}
              inclinedVecs={inclinedVecs}
              coplanarVecs={coplanarVecs}
              gimbalVecs={gimbalVecs}
              showInclined={showInclined}
              showCoplanar={showCoplanar}
              showGimbal={showGimbal}
              halfU={halfU}
              halfV={halfV}
              theta={theta}
              numAngles={params.numAngles}
              sdd={params.sdd}
            />
          </div>
          <div className="canvas-pane canvas-pane-bottom">
            <FovCanvas
              fovResults={fovResults}
              gridHalf={gridHalf}
              showInclined={showInclined}
              showCoplanar={showCoplanar}
              showGimbal={showGimbal}
              showAllFootprints={showAllFootprints}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
